import React from "react";

import { StripeConnectIntegration } from "@editor/components/shops-ui/settings/shop/integrations/StripeConnectIntegration";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useModal } from "@editor/hooks/useModal";
import { stripeConnectRouter } from "@editor/trpc/stripeConnect";

import { ProductCheckoutLink } from "@/features/products/components/ProductCheckoutLink";
import { ProductDeliveryEmail } from "@/features/products/components/ProductDeliveryEmail";
import { ProductEditForm } from "@/features/products/components/ProductEditForm";
import { ProductImageCard } from "@/features/products/components/ProductImageCard";
import { useProductFormEdit } from "@/features/products/hooks/useProductFormEdit";
import Button from "@replo/design-system/components/button/Button";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

export function ProductEditTab() {
  const { project } = useCurrentProjectContext();
  const { productId } = useParams<{ productId: string }>();
  const { creatingNewProduct, watch } = useProductFormEdit();
  const { openModal } = useModal();
  const isMobile = useIsMobile();

  const { data: onboardingStatus, isLoading: isLoadingOnboardingStatus } =
    stripeConnectRouter.stripeConnectGetOnboardingStatus.useQuery(
      project?.id ? { projectId: project.id } : skipToken,
    );

  const isConnected =
    onboardingStatus?.isConnected && onboardingStatus?.onboardingCompleted;
  const checkoutMethod = watch("checkoutMethod");

  return (
    <div
      className={twMerge(
        "h-full overflow-y-auto no-scrollbar",
        isMobile
          ? "flex flex-col gap-4 px-4 py-6"
          : "flex justify-center gap-4",
      )}
    >
      {isMobile && <ProductImageCard />}
      <div className="flex flex-col gap-4">
        <ProductEditForm />
        <ProductCheckoutLink />
        {checkoutMethod === "replo" &&
          project &&
          !isLoadingOnboardingStatus && (
            <div className="w-full">
              {isConnected ? (
                <ProductDeliveryEmail />
              ) : (
                <StripeConnectIntegration variant="black-box" />
              )}
            </div>
          )}
        {!creatingNewProduct && productId && project && (
          <Button
            variant="tertiary"
            size="base"
            onClick={() =>
              openModal({
                type: "productDeleteConfirmationModal",
                props: {
                  productId,
                  projectId: project.id,
                },
              })
            }
            UNSAFE_className="text-danger"
          >
            Delete Product
          </Button>
        )}
      </div>
      {!isMobile && <ProductImageCard />}
    </div>
  );
}
